/* mo-base.js: the bed builder on the foundations and bed frames pages (sections/mo-base.liquid).
   - Reads the section's JSON (live variant prices of both collections, piece specs from the section blocks).
   - Size, piece and mattress controls redraw the side and top views (assets/mo-base-draw.js) and reprice every
     card and row for the chosen size.
   - First view: the pieces drop onto each other once, with weight (ported from 21st.dev Toast Gravity Stack 33302:
     real gravity, impact squash on a damped spring after Spring Element 2923, a small bounce, then the stack
     freezes). After that the drawing only moves when the shopper changes something.
   - DOM writes: the drawing is patched attribute by attribute from a detached parse, and text changes go through
     text nodes, so assets/custom.js' body MutationObserver (childList) is not re-triggered on every frame.
   Reduced motion: no drop, no spring; the end state is drawn at once. */
(function () {
  'use strict';

  var G = 386; /* gravity in inches per second squared */
  var SQ_K = 300, SQ_C = 2 * 0.45 * Math.sqrt(300); /* squash spring (Spring Element 2923: stiffness 200, damping 16, retuned) */
  var H_K = 170, H_C = 2 * 0.86 * Math.sqrt(170); /* height changes settle without overshooting far */
  var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;

  function $(r, s) { return r.querySelector(s); }
  function $$(r, s) { return Array.prototype.slice.call(r.querySelectorAll(s)); }
  function setText(el, t) {
    if (!el) return;
    t = String(t);
    if (el.childNodes.length === 1 && el.firstChild.nodeType === 3) { if (el.firstChild.nodeValue !== t) el.firstChild.nodeValue = t; }
    else if (el.textContent !== t) el.textContent = t;
  }
  function setAttr(el, k, v) { if (el && el.getAttribute(k) !== v) el.setAttribute(k, v); }
  function toggle(el, on) { if (!el) return; if (on) { if (el.hasAttribute('hidden')) el.removeAttribute('hidden'); } else if (!el.hasAttribute('hidden')) el.setAttribute('hidden', ''); }
  function money(c) {
    var d = c / 100;
    return '$' + (c % 100 === 0 ? d.toLocaleString('en-US') : d.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  }

  /* Patch a live SVG group from fresh markup: same structure, copy changed attributes and text; else replace. */
  var parser = new DOMParser();
  function shape(a, b) {
    if (a.childNodes.length !== b.childNodes.length) return false;
    for (var i = 0; i < a.childNodes.length; i++) {
      var x = a.childNodes[i], y = b.childNodes[i];
      if (x.nodeType !== y.nodeType || (x.nodeType === 1 && (x.nodeName !== y.nodeName || !shape(x, y)))) return false;
    }
    return true;
  }
  function copy(a, b) {
    for (var i = 0; i < a.childNodes.length; i++) {
      var x = a.childNodes[i], y = b.childNodes[i];
      if (x.nodeType === 3) { if (x.nodeValue !== y.nodeValue) x.nodeValue = y.nodeValue; continue; }
      if (x.nodeType !== 1) continue;
      var ya = y.attributes;
      for (var k = 0; k < ya.length; k++) if (x.getAttribute(ya[k].name) !== ya[k].value) x.setAttribute(ya[k].name, ya[k].value);
      for (var j = x.attributes.length - 1; j >= 0; j--) if (!y.hasAttribute(x.attributes[j].name)) x.removeAttribute(x.attributes[j].name);
      copy(x, y);
    }
  }
  function patch(g, markup) {
    var doc = parser.parseFromString('<svg xmlns="http://www.w3.org/2000/svg"><g>' + markup + '</g></svg>', 'image/svg+xml');
    var fresh = doc.documentElement.firstChild;
    if (shape(g, fresh)) { copy(g, fresh); return; }
    while (g.firstChild) g.removeChild(g.firstChild);
    while (fresh.firstChild) g.appendChild(document.importNode(fresh.firstChild, true)), fresh.removeChild(fresh.firstChild);
  }

  function init(root) {
    var D = window.MOBaseDraw;
    var dataEl = $(root, '[data-mb-data]');
    if (!D || !dataEl || root.__mb) return;
    root.__mb = true;
    var cfg;
    try { cfg = JSON.parse(dataEl.textContent); } catch (e) { return; }
    var pieces = cfg.pieces || {}, products = cfg.products || {};
    var st = { size: cfg.size, frame: cfg.frame || null, foundation: cfg.foundation || null, mattressIn: +cfg.mattressIn, frameIn: +cfg.frameIn, fdIn: +cfg.fdIn };
    if (st.frame && !pieces[st.frame]) st.frame = null;
    if (st.foundation && !pieces[st.foundation]) st.foundation = null;

    var sideG = $(root, '[data-mb-side-g]'), planG = $(root, '[data-mb-plan-g]');
    var bigN = $(root, '[data-mb-big]'), bigF = $(root, '[data-mb-big-f]'), bigPlain = $(root, '[data-mb-big-plain]'), sum = $(root, '[data-mb-sum]');
    var sideTitle = $(root, '[data-mb-side-title]'), live = $(root, '[data-mb-live]');
    var matIn = $(root, '[data-mb-mat]'), matOut = $(root, '[data-mb-mat-out]');
    var frIn = $(root, '[data-mb-frame-in]'), frOut = $(root, '[data-mb-frame-out]'), frBox = $(root, '[data-mb-yours-frame]');
    var fdIn = $(root, '[data-mb-fd-in]'), fdOut = $(root, '[data-mb-fd-out]'), fdBox = $(root, '[data-mb-yours-fd]'), yours = $(root, '[data-mb-yours]');

    /* shown heights spring toward the targets; drop holds per-piece fall state */
    function target() {
      var fr = st.frame && pieces[st.frame], fd = st.foundation && pieces[st.foundation];
      return {
        frame: fr ? (fr.h != null ? fr.h : st.frameIn) : 0,
        foundation: fd ? (fd.h != null ? fd.h : st.fdIn) : 0,
        mattress: st.mattressIn
      };
    }
    var shown = target(), vel = { frame: 0, foundation: 0, mattress: 0 };
    var ghost = { frame: st.frame, foundation: st.foundation }; /* keeps a removed piece drawn while it shrinks away */
    var drop = null, raf = 0, last = 0;

    function drawState() {
      return {
        size: st.size, frameIn: st.frameIn, fdIn: st.fdIn, mattressIn: st.mattressIn,
        frame: st.frame || (shown.frame > 0.2 ? ghost.frame : null),
        foundation: st.foundation || (shown.foundation > 0.2 ? ghost.foundation : null),
        h: { frame: shown.frame, foundation: shown.foundation, mattress: shown.mattress }
      };
    }

    function inchWords(v) { return D.inch(v) + ' in.'; }

    function paint() {
      var ds = drawState();
      var anim = drop ? drop.anim() : null;
      patch(sideG, D.elevation(ds, pieces, anim));
      var T = target(), total = T.frame + T.foundation + T.mattress;
      var showTotal = shown.frame * (ds.frame ? 1 : 0) + shown.foundation * (ds.foundation ? 1 : 0) + shown.mattress;
      var q = Math.round(showTotal * 4) / 4, whole = Math.floor(q), fr = D.inch(q - whole);
      setText(bigN, String(whole));
      setText(bigF, fr === '0' ? '' : fr);
    }

    function words() {
      var T = target(), parts = [], fr = st.frame && pieces[st.frame], fd = st.foundation && pieces[st.foundation];
      if (fr) parts.push(inchWords(T.frame) + ' ' + (fr.short || 'frame') + (fr.h == null ? ' (your measurement)' : ''));
      else parts.push('no frame');
      if (fd) parts.push(inchWords(T.foundation) + ' ' + (fd.short || 'foundation') + (fd.h == null ? ' (your measurement)' : ''));
      parts.push(inchWords(T.mattress) + ' mattress');
      return { total: T.frame + T.foundation + T.mattress, parts: parts };
    }

    function settle() {
      /* everything that does not animate: plan, prices, notes, readout text */
      patch(planG, D.plan(drawState(), pieces));
      var w = words();
      setText(sum, w.parts.join(' + '));
      setText(bigPlain, D.inch(w.total) + ' inches');
      setText(sideTitle, 'Side view, to scale: ' + w.parts.join(', ') + '. ' + D.inch(w.total) + ' in. from the floor to the top of the mattress.');
      facts();
      yoursUI();
    }

    function facts() {
      var fr = st.frame && pieces[st.frame], dims = D.SIZES[st.size] || [60, 79], W = dims[0];
      var on = {
        all: true,
        center: W >= 60,
        small: W < 60,
        king: st.size === 'King' && !!st.foundation,
        platform: !!(fr && fr.style === 'platform'),
        rails: !!(fr && fr.style === 'rail'),
        fit: !!(fr && fr.maxW && W > fr.maxW)
      };
      $$(root, '[data-fact]').forEach(function (li) {
        var k = li.getAttribute('data-fact');
        toggle(li, !!on[k]);
      });
    }

    function yoursUI() {
      var fr = st.frame && pieces[st.frame], fd = st.foundation && pieces[st.foundation];
      var nf = !!(fr && fr.h == null), nd = !!(fd && fd.h == null);
      toggle(frBox, nf); toggle(fdBox, nd); toggle(yours, nf || nd);
      if (nf) setText($(frBox, '[data-mb-yours-name]'), fr.short);
      if (nd) setText($(fdBox, '[data-mb-yours-name]'), fd.short);
    }

    function reprice() {
      $$(root, '[data-mb-price]').forEach(function (box) {
        var p = products[box.getAttribute('data-mb-price')];
        if (!p) return;
        var key = p.any ? Object.keys(p.v)[0] : st.size, v = p.v[key];
        var now = $(box, '[data-mb-now]'), was = $(box, '[data-mb-was]');
        if (v) {
          setText(now, money(v[0]));
          now.classList.toggle('mb-price__now--none', false);
          if (v[1] > v[0]) {
            if (!was.firstChild) was.innerHTML = '<span class="mb-vh">Compare at </span>' + money(v[1]);
            else setText(was.lastChild, money(v[1]));
            toggle(was, true);
          } else toggle(was, false);
        } else {
          setText(now, 'Not made in ' + st.size);
          now.classList.toggle('mb-price__now--none', true);
          toggle(was, false);
        }
        var card = box.closest('[data-mb-card], [data-mb-row]');
        if (card) card.classList.toggle('is-out', !v);
      });
      $$(root, '[data-mb-size-word]').forEach(function (el) { setText(el, st.size); });
    }

    /* ---------------------------------------------------------------- motion */
    function moving() {
      var T = target();
      for (var k in T) if (Math.abs(shown[k] - T[k]) > 0.01 || Math.abs(vel[k]) > 0.05) return true;
      return !!drop;
    }
    function step(now) {
      raf = 0;
      var dt = Math.min(0.034, Math.max(0.001, (now - (last || now)) / 1000));
      last = now;
      var T = target();
      for (var k in T) {
        var a = -H_K * (shown[k] - T[k]) - H_C * vel[k];
        vel[k] += a * dt; shown[k] += vel[k] * dt;
        if (Math.abs(shown[k] - T[k]) < 0.01 && Math.abs(vel[k]) < 0.05) { shown[k] = T[k]; vel[k] = 0; }
      }
      if (drop && drop.step(dt)) drop = null;
      paint();
      if (moving()) raf = requestAnimationFrame(step);
      else { last = 0; settle(); }
    }
    function kick() {
      if (reduce) { shown = target(); vel = { frame: 0, foundation: 0, mattress: 0 }; drop = null; paint(); settle(); return; }
      if (!raf) { last = 0; raf = requestAnimationFrame(step); }
    }

    /* The first-view drop: frame, then foundation, then mattress fall from above the stage and land in turn. */
    function Drop(keys) {
      var bodies = keys.map(function (k, i) {
        return { key: k, dy: 46 + i * 14, vy: 0, sy: 0, vs: 0, wait: i * 0.2, e: [0.08, 0.14, 0.22][i] || 0.18, landed: false, calm: 0 };
      });
      this.anim = function () {
        var out = {}, below = 0;
        bodies.forEach(function (b) {
          var h = shown[b.key] || 0;
          out[b.key] = { dy: b.dy - below, sy: b.sy };
          below += b.sy * h;
        });
        return out;
      };
      this.step = function (dt) {
        var done = true;
        bodies.forEach(function (b) {
          if (b.wait > 0) { b.wait -= dt; done = false; return; }
          if (b.dy > 0 || b.vy > 0) {
            b.vy -= G * dt; b.dy += b.vy * dt;
            if (b.dy <= 0) {
              var v = -b.vy; b.dy = 0;
              b.vs += v * 0.011; /* the impact squashes the piece */
              b.vy = v > 14 ? v * b.e : 0; /* a small bounce, then it stays */
              b.landed = true;
            }
          }
          var a = -SQ_K * b.sy - SQ_C * b.vs;
          b.vs += a * dt; b.sy += b.vs * dt;
          b.sy = Math.max(-0.08, Math.min(0.16, b.sy));
          if (b.landed && b.dy === 0 && b.vy === 0 && Math.abs(b.sy) < 0.002 && Math.abs(b.vs) < 0.02) { if (++b.calm > 10) { b.sy = 0; b.vs = 0; } else done = false; }
          else done = false;
        });
        return done;
      };
    }

    function startDrop() {
      if (reduce) return;
      var keys = [];
      if (st.frame) keys.push('frame');
      if (st.foundation) keys.push('foundation');
      keys.push('mattress');
      if (root.getAttribute('data-mode') === 'foundations' && keys[0] === 'frame') keys.shift(); /* the foundations page drops the foundation and the mattress onto a standing frame */
      drop = new Drop(keys);
      kick();
    }

    /* ---------------------------------------------------------------- controls */
    function announce(t) { setText(live, t); }
    function changed(why) {
      if (why === 'size') { reprice(); announce('Prices now for ' + st.size + '.'); }
      else { var w = words(); announce(D.inch(w.total) + ' inches from the floor to the top of the mattress.'); }
      settle();
      kick();
    }
    function setPiece(kind, handle, drop1) {
      handle = handle || null;
      if (st[kind] === handle) return;
      if (st[kind]) ghost[kind] = st[kind];
      st[kind] = handle;
      if (handle) ghost[kind] = handle;
      $$(root, 'input[data-mb-piece="' + kind + '"]').forEach(function (r) { r.checked = (r.value || null) === handle; });
      if (handle && drop1 && !reduce) {
        /* the new piece and everything above it fall into place on the final stack */
        var keys = kind === 'frame' ? ['frame'].concat(st.foundation ? ['foundation'] : [], ['mattress']) : ['foundation', 'mattress'];
        var T = target();
        keys.forEach(function (k) { shown[k] = T[k]; vel[k] = 0; });
        drop = new Drop(keys);
      }
      changed('piece');
    }

    root.addEventListener('change', function (e) {
      var t = e.target;
      if (t.hasAttribute('data-mb-size')) { st.size = t.value; changed('size'); }
      else if (t.hasAttribute('data-mb-piece')) setPiece(t.getAttribute('data-mb-piece'), t.value, true);
    });
    function setMat(v, from) {
      v = Math.max(6, Math.min(18, Math.round(v * 4) / 4));
      st.mattressIn = v;
      if (from !== 'range') matIn.value = v;
      setText(matOut, D.inch(v) + ' in.');
      $$(root, '[data-mb-quick]').forEach(function (b) { setAttr(b, 'aria-pressed', String(Math.abs(parseFloat(b.getAttribute('data-mb-quick')) - v) < 0.01)); });
      changed('piece');
    }
    if (matIn) matIn.addEventListener('input', function () { setMat(parseFloat(matIn.value), 'range'); });
    $$(root, '[data-mb-quick]').forEach(function (b) {
      b.addEventListener('click', function () { setMat(parseFloat(b.getAttribute('data-mb-quick'))); });
    });
    if (frIn) frIn.addEventListener('input', function () { st.frameIn = parseFloat(frIn.value); setText(frOut, D.inch(st.frameIn) + ' in.'); changed('piece'); });
    if (fdIn) fdIn.addEventListener('input', function () { st.fdIn = parseFloat(fdIn.value); setText(fdOut, D.inch(st.fdIn) + ' in.'); changed('piece'); });
    $$(root, '[data-mb-use]').forEach(function (b) {
      b.addEventListener('click', function () {
        setPiece(b.getAttribute('data-mb-kind'), b.getAttribute('data-mb-use'), true);
        var stage = $(root, '[data-mb-stage]');
        if (stage) stage.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      });
    });

    /* first state: quick-pick pressed state, prices, then the drop on first view */
    setMat(st.mattressIn);
    reprice();
    settle();
    paint();
    var stage = $(root, '[data-mb-stage]');
    if ('IntersectionObserver' in window && stage) {
      var io = new IntersectionObserver(function (es) {
        if (es.some(function (e) { return e.isIntersecting; })) { io.disconnect(); startDrop(); }
      }, { threshold: 0.3 });
      io.observe(stage);
    }
    /* phones fold the top view, each row's facts and the sources (CSS applies the fold only below 600 px and only
       once this class is set, so without scripts everything stays open) */
    function fold(box, open) {
      box.classList.toggle('is-open', open);
      var btn = $(box, '[data-mb-fold-btn]');
      if (btn) setAttr(btn, 'aria-expanded', String(open));
    }
    root.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-mb-fold-btn]');
      if (btn && root.contains(btn)) { var box = btn.closest('[data-mb-fold]'); fold(box, !box.classList.contains('is-open')); return; }
      var fn = e.target.closest && e.target.closest('.mb-fn');
      if (fn) { var src = $(root, '.mb-fold--src'); if (src) fold(src, true); }
    });
    root.classList.add('is-live');
  }

  function boot() { Array.prototype.forEach.call(document.querySelectorAll('[data-mb]'), init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
